import React from 'react';
import { Link } from 'react-router-dom';
import { Ruler, Sparkles, Shield, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqCategories = [
  {
    id: 'sizing',
    icon: Ruler,
    title: 'Sizing & Fit',
    questions: [
      {
        q: 'What if my gear doesn\'t fit?',
        a: 'We offer one free size exchange if requested within 24 hours of delivery. Simply contact our support team with your order number and the size you need, and we\'ll ship the replacement immediately at no additional cost.'
      },
      {
        q: 'How do I know what size to order?',
        a: 'Each product page includes a detailed size chart with measurements. We recommend measuring yourself and comparing to our charts. If you\'re between sizes, we generally recommend going up for helmets (for comfort liners to break in) and jackets (for base layers underneath).'
      },
      {
        q: 'Can I try multiple sizes?',
        a: 'For your first rental, we recommend choosing your best-guess size based on our charts. If it doesn\'t fit, use your free size exchange. Repeat customers with a size history get expedited exchanges.'
      },
      {
        q: 'Do helmets run true to size?',
        a: 'Helmet sizing varies by brand. AGV tends to run small, Shoei is true to size, and Arai runs slightly large. Check the specific product notes for brand-specific guidance.'
      }
    ]
  },
  {
    id: 'cleaning',
    icon: Sparkles,
    title: 'Cleaning & Hygiene',
    questions: [
      {
        q: 'How is the gear cleaned between rentals?',
        a: 'Every item is professionally sanitized and inspected after every use. Helmets are treated with medical-grade sanitizing agents, liners are steam-cleaned, and leather jackets are conditioned and wiped down. All gear passes a 15-point inspection before being shipped.'
      },
      {
        q: 'Do I need to clean the gear before returning?',
        a: 'No cleaning is required on your end. Simply pack the gear back in the provided bag and use the prepaid shipping label. We handle all cleaning and maintenance.'
      },
      {
        q: 'What if the gear gets dirty during my rental?',
        a: 'Normal wear and dirt is expected and covered by our cleaning process. Just return the gear as-is. Excessive damage or stains may incur additional cleaning fees (detailed in our Terms of Service).'
      },
      {
        q: 'Are the helmet liners replaced?',
        a: 'Helmet liners are professionally cleaned after each use. If a liner shows signs of wear beyond cleaning, we replace it before the next rental. You\'ll always receive fresh, clean gear.'
      }
    ]
  },
  {
    id: 'deposits',
    icon: Shield,
    title: 'Security Deposits & Payments',
    questions: [
      {
        q: 'Is there a security deposit?',
        a: 'Yes, a temporary hold is placed on your card at checkout. This hold varies based on the total value of the gear rented (typically 20-30% of retail value) and is released once gear is returned and inspected, usually within 3-5 business days.'
      },
      {
        q: 'When is the deposit released?',
        a: 'The security deposit hold is released within 3-5 business days after we receive and inspect your returned gear. You\'ll receive an email confirmation once the hold is released.'
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure Shopify checkout. Debit cards are also accepted but may temporarily reduce your available balance during the hold period.'
      },
      {
        q: 'What if gear is returned late?',
        a: 'Late returns are charged at 1.5x the daily rental rate for each day past the return date. We send reminder emails before your return date and on the due date to help you avoid late fees.'
      },
      {
        q: 'What if gear is lost or stolen?',
        a: 'Lost or stolen gear will be charged the replacement cost, which varies by item. Replacement costs are detailed on each product page and in our Terms of Service. We recommend keeping rental gear in a secure location.'
      }
    ]
  },
  {
    id: 'general',
    icon: HelpCircle,
    title: 'General Questions',
    questions: [
      {
        q: 'How far in advance should I book?',
        a: 'We recommend booking at least 5-7 days before your rental start date to ensure availability and delivery. Popular sizes and peak seasons may require more advance notice.'
      },
      {
        q: 'Can I extend my rental?',
        a: 'Yes! Contact us before your rental end date to extend. Extensions are subject to availability and charged at the standard daily rate. We\'ll send you a new return label if needed.'
      },
      {
        q: 'Do you ship internationally?',
        a: 'Currently, we only ship within the continental United States. We\'re working on expanding to Canada and other regions. Sign up for our newsletter to be notified when international shipping becomes available.'
      },
      {
        q: 'What brands do you carry?',
        a: 'We carry premium brands including AGV, Shoei, Arai, Dainese, Alpinestars, REV\'IT!, and Knox. All gear is current-season and CE certified.'
      },
      {
        q: 'Can I purchase gear I\'ve rented?',
        a: 'Yes! If you fall in love with your rental gear, contact us about our rent-to-own program. A portion of your rental fees can be applied toward purchase of new gear from our partners.'
      }
    ]
  }
];

const FAQPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24" data-testid="faq-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 py-16 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">Support</p>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold uppercase tracking-tight mb-6 text-gray-900">
            Rental <span className="text-[#C6A87C]">FAQ</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Everything you need to know about renting premium motorcycle gear. Can't find your answer? Contact our support team.
          </p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="px-6 md:px-12 py-16">
        <div className="max-w-4xl mx-auto">
          {faqCategories.map((category) => (
            <div key={category.id} className="mb-12" data-testid={`faq-${category.id}`}>
              {/* Category Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center">
                  <category.icon className="w-6 h-6 text-[#C6A87C]" strokeWidth={1.5} />
                </div>
                <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase tracking-tight text-gray-900">
                  {category.title}
                </h2>
              </div>

              {/* Questions */}
              <Accordion type="single" collapsible className="space-y-2">
                {category.questions.map((item, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`${category.id}-${index}`}
                    className="bg-gray-50 border border-gray-200 px-6 data-[state=open]:border-[#C6A87C]/30"
                  >
                    <AccordionTrigger className="text-left py-5 hover:text-[#C6A87C] hover:no-underline text-gray-900">
                      <span className="font-semibold">{item.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="px-6 md:px-12 py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4 text-gray-900">
            Still Have Questions?
          </h2>
          <p className="text-gray-500 mb-8 max-w-xl mx-auto">
            Our support team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:support@ridegear.com">
              <Button 
                className="bg-[#C6A87C] text-black hover:bg-[#D4B88F] font-bold uppercase tracking-wider px-8 py-6"
                data-testid="contact-support-btn"
              >
                Contact Support
              </Button>
            </a>
            <Link to="/terms">
              <Button 
                variant="outline"
                className="border-gray-300 hover:border-[#C6A87C] hover:text-[#C6A87C] text-gray-700 font-bold uppercase tracking-wider px-8 py-6"
                data-testid="view-terms-btn"
              >
                View Terms of Service
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
